/**
 * EditorFilters - Reusable filter presets & canvas utilities for image editing
 * 
 * This module provides:
 * - Predefined CSS filter presets for image editing
 * - Canvas utility functions for applying crops, rotations, flips, and filters
 * - Helper functions for generating cropped/edited images as blobs or data URLs
 * 
 * Filters are applied via ImageData pixel manipulation (NOT ctx.filter) for full
 * Safari compatibility. ctx.filter was only added to Safari in Safari 18 (Sep 2024)
 * and is silently ignored in older versions.
 * 
 * Usage:
 *   import { FILTER_PRESETS, applyEditsToCanvas, generateEditedImage } from '../commons/media-editor/EditorFilters'
 */

// ─────────────────────────────────────────────────────────────
// Filter Presets
// ─────────────────────────────────────────────────────────────

export const FILTER_PRESETS = [
    {
        id: 'original',
        name: 'Original',
        filter: 'none',
    },
    {
        id: 'vivid',
        name: 'Vivid',
        filter: 'saturate(1.4) contrast(1.1)',
    },
    {
        id: 'warm',
        name: 'Warm',
        filter: 'sepia(0.25) saturate(1.2) brightness(1.05)',
    },
    {
        id: 'cool',
        name: 'Cool',
        filter: 'saturate(0.9) brightness(1.05) hue-rotate(15deg)',
    },
    {
        id: 'bright',
        name: 'Bright',
        filter: 'brightness(1.2) contrast(1.05)',
    },
    {
        id: 'contrast',
        name: 'Contrast',
        filter: 'contrast(1.4) brightness(1.05)',
    },
    {
        id: 'grayscale',
        name: 'B&W',
        filter: 'grayscale(1)',
    },
    {
        id: 'sepia',
        name: 'Sepia',
        filter: 'sepia(0.8)',
    },
    {
        id: 'vintage',
        name: 'Vintage',
        filter: 'sepia(0.4) contrast(1.1) brightness(0.95) saturate(0.8)',
    },
    {
        id: 'dramatic',
        name: 'Dramatic',
        filter: 'contrast(1.5) brightness(0.9) saturate(1.2)',
    },
    {
        id: 'fade',
        name: 'Fade',
        filter: 'contrast(0.85) brightness(1.1) saturate(0.8)',
    },
    {
        id: 'noir',
        name: 'Noir',
        filter: 'grayscale(1) contrast(1.3) brightness(0.9)',
    },
    {
        id: 'dreamy',
        name: 'Dreamy',
        // blur() is omitted here intentionally — pixel-level blur via ImageData
        // is prohibitively expensive and the 0.5px blur is barely perceptible.
        filter: 'brightness(1.1) contrast(0.9) saturate(1.2)',
    },
    {
        id: 'sharp',
        name: 'Sharp',
        filter: 'contrast(1.2) saturate(1.1) brightness(1.05)',
    },
];

// ─────────────────────────────────────────────────────────────
// Aspect Ratio Presets
// ─────────────────────────────────────────────────────────────

export const ASPECT_RATIOS = [
    { id: 'free', name: 'Free', value: undefined },
    { id: '1:1', name: '1:1', value: 1 },
    { id: '4:3', name: '4:3', value: 4 / 3 },
    { id: '3:4', name: '3:4', value: 3 / 4 },
    { id: '16:9', name: '16:9', value: 16 / 9 },
    { id: '9:16', name: '9:16', value: 9 / 16 },
];

// ─────────────────────────────────────────────────────────────
// Canvas Utility: Create Image from Source
// ─────────────────────────────────────────────────────────────

/**
 * Load an image from a source URL and return an HTMLImageElement
 * @param {string} src - Image source (data URL, blob URL, or regular URL)
 * @returns {Promise<HTMLImageElement>}
 */
export const loadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = src;
    });
};

// ─────────────────────────────────────────────────────────────
// ImageData Pixel Filter Implementation
// (Cross-browser, including Safari < 18 which ignores ctx.filter)
// ─────────────────────────────────────────────────────────────

/** Clamp a value to [0, 255] */
const clamp = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);

/**
 * Parse a CSS filter string into an array of {fn, value} operations.
 * e.g. "sepia(0.4) contrast(1.1) brightness(0.95)"
 *   → [{fn:'sepia',value:0.4}, {fn:'contrast',value:1.1}, {fn:'brightness',value:0.95}]
 */
const parseFilterOps = (filterCSS) => {
    if (!filterCSS || filterCSS === 'none') return [];
    const regex = /([\w-]+)\(([^)]+)\)/g;
    const ops = [];
    let match;
    while ((match = regex.exec(filterCSS)) !== null) {
        const fn = match[1];
        const argStr = match[2].trim();
        const value = parseFloat(argStr); // works for deg, px, plain number
        ops.push({ fn, value });
    }
    return ops;
};

/** brightness(amount) — amount=1 is normal, >1 brighter, <1 darker */
const applyBrightness = (data, amount) => {
    for (let i = 0; i < data.length; i += 4) {
        data[i]     = clamp(data[i]     * amount);
        data[i + 1] = clamp(data[i + 1] * amount);
        data[i + 2] = clamp(data[i + 2] * amount);
    }
};

/** contrast(amount) — amount=1 is normal, >1 more contrast */
const applyContrast = (data, amount) => {
    for (let i = 0; i < data.length; i += 4) {
        data[i]     = clamp((data[i]     - 128) * amount + 128);
        data[i + 1] = clamp((data[i + 1] - 128) * amount + 128);
        data[i + 2] = clamp((data[i + 2] - 128) * amount + 128);
    }
};

/** grayscale(amount) — amount=0 is original, 1 is full grayscale */
const applyGrayscale = (data, amount) => {
    for (let i = 0; i < data.length; i += 4) {
        const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        data[i]     = clamp(data[i]     + (lum - data[i])     * amount);
        data[i + 1] = clamp(data[i + 1] + (lum - data[i + 1]) * amount);
        data[i + 2] = clamp(data[i + 2] + (lum - data[i + 2]) * amount);
    }
};

/** sepia(amount) — amount=0 is original, 1 is full sepia */
const applySepia = (data, amount) => {
    for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i + 1], b = data[i + 2];
        const sr = clamp(r * 0.393 + g * 0.769 + b * 0.189);
        const sg = clamp(r * 0.349 + g * 0.686 + b * 0.168);
        const sb = clamp(r * 0.272 + g * 0.534 + b * 0.131);
        data[i]     = clamp(r + (sr - r) * amount);
        data[i + 1] = clamp(g + (sg - g) * amount);
        data[i + 2] = clamp(b + (sb - b) * amount);
    }
};

/** saturate(amount) — amount=1 is normal, 0=grayscale, >1 more vivid */
const applySaturate = (data, amount) => {
    for (let i = 0; i < data.length; i += 4) {
        const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        data[i]     = clamp(lum + (data[i]     - lum) * amount);
        data[i + 1] = clamp(lum + (data[i + 1] - lum) * amount);
        data[i + 2] = clamp(lum + (data[i + 2] - lum) * amount);
    }
};

// RGB ↔ HSL helpers for hue-rotate
const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
};

const rgbToHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            default: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return [h, s, l];
};

const hslToRgb = (h, s, l) => {
    if (s === 0) {
        const v = Math.round(l * 255);
        return [v, v, v];
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [
        Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
        Math.round(hue2rgb(p, q, h) * 255),
        Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    ];
};

/** hue-rotate(degrees) */
const applyHueRotate = (data, degrees) => {
    const shift = (degrees / 360 + 1) % 1; // normalize to [0,1]
    for (let i = 0; i < data.length; i += 4) {
        const [h, s, l] = rgbToHsl(data[i], data[i + 1], data[i + 2]);
        const [r, g, b] = hslToRgb((h + shift) % 1, s, l);
        data[i]     = r;
        data[i + 1] = g;
        data[i + 2] = b;
    }
};

/**
 * Apply CSS-style filter string to a canvas using ImageData pixel manipulation.
 * This is fully cross-browser and works in Safari < 18, which silently ignores ctx.filter.
 *
 * Supported operations: brightness, contrast, grayscale, sepia, saturate, hue-rotate.
 * blur() is intentionally skipped (minor visual effect, expensive via ImageData).
 *
 * @param {HTMLCanvasElement} canvas - The canvas to apply filters to (modified in-place)
 * @param {string} filterCSS - CSS filter string, e.g. 'grayscale(1) contrast(1.3)'
 * @returns {HTMLCanvasElement} - The same canvas (modified in-place)
 */
const applyImageDataFilter = (canvas, filterCSS) => {
    if (!filterCSS || filterCSS === 'none') return canvas;
    const ops = parseFilterOps(filterCSS);
    if (ops.length === 0) return canvas;

    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { data } = imageData;

    for (const { fn, value } of ops) {
        if      (fn === 'brightness')   applyBrightness(data, value);
        else if (fn === 'contrast')     applyContrast(data, value);
        else if (fn === 'grayscale')    applyGrayscale(data, value);
        else if (fn === 'sepia')        applySepia(data, value);
        else if (fn === 'saturate')     applySaturate(data, value);
        else if (fn === 'hue-rotate')   applyHueRotate(data, value);
        // blur, opacity, invert, etc.: not implemented
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
};

// ─────────────────────────────────────────────────────────────
// Canvas Utility: Get Rotated + Flipped Image
// ─────────────────────────────────────────────────────────────

/**
 * Rotate and flip an image on a canvas
 * @param {HTMLImageElement} image 
 * @param {number} rotation - Degrees
 * @param {Object} flip - { horizontal, vertical }
 * @returns {HTMLCanvasElement}
 */
const getRotatedImage = (image, rotation, flip) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const radians = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(radians));
    const cos = Math.abs(Math.cos(radians));

    // Calculate new dimensions after rotation
    const newWidth = image.width * cos + image.height * sin;
    const newHeight = image.width * sin + image.height * cos;

    canvas.width = newWidth;
    canvas.height = newHeight;

    // Move to center, rotate, then draw
    ctx.translate(newWidth / 2, newHeight / 2);
    ctx.rotate(radians);

    // Apply flip
    const scaleX = flip.horizontal ? -1 : 1;
    const scaleY = flip.vertical ? -1 : 1;
    ctx.scale(scaleX, scaleY);

    ctx.drawImage(image, -image.width / 2, -image.height / 2);

    return canvas;
};

// ─────────────────────────────────────────────────────────────
// Canvas Utility: Get Cropped & Rotated Image
// ─────────────────────────────────────────────────────────────

/**
 * Extract a cropped area from a rotated image and apply filters via pixel manipulation.
 * Does NOT use ctx.filter (unsupported in Safari < 18).
 * 
 * @param {string} imageSrc - Source image URL/data URL
 * @param {Object} cropArea - Crop area from react-easy-crop { x, y, width, height }
 * @param {number} rotation - Rotation in degrees
 * @param {Object} flip - Flip state { horizontal: boolean, vertical: boolean }
 * @param {string} filterCSS - CSS filter string to apply (e.g., 'grayscale(1) contrast(1.3)')
 * @returns {Promise<HTMLCanvasElement>} - Canvas with the final edited image
 */
export const applyEditsToCanvas = async (imageSrc, cropArea, rotation = 0, flip = { horizontal: false, vertical: false }, filterCSS = 'none') => {
    const image = await loadImage(imageSrc);

    // Step 1: Create a canvas with the rotated (uncropped) image
    const rotatedCanvas = getRotatedImage(image, rotation, flip);

    // Step 2: Crop from the rotated canvas — draw WITHOUT ctx.filter (Safari < 18 incompatible)
    const croppedCanvas = document.createElement('canvas');
    const ctx = croppedCanvas.getContext('2d');

    croppedCanvas.width = cropArea.width;
    croppedCanvas.height = cropArea.height;

    ctx.drawImage(
        rotatedCanvas,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        cropArea.width,
        cropArea.height
    );

    // Step 3: Apply filter via ImageData pixel manipulation (works in ALL browsers including Safari)
    applyImageDataFilter(croppedCanvas, filterCSS);

    return croppedCanvas;
};

// ─────────────────────────────────────────────────────────────
// Main Export: Generate Edited Image
// ─────────────────────────────────────────────────────────────

/**
 * Generate the final edited image as a Blob and data URL
 * This is the main function to call after the user clicks "Apply"
 * 
 * @param {string} imageSrc - Source image URL/data URL
 * @param {Object} cropArea - Crop area from react-easy-crop { x, y, width, height }
 * @param {number} rotation - Rotation in degrees (default: 0)
 * @param {Object} flip - Flip state { horizontal: boolean, vertical: boolean }
 * @param {string} filterCSS - CSS filter string (default: 'none')
 * @param {string} outputFormat - Output MIME type (default: 'image/jpeg')
 * @param {number} quality - Output quality 0-1 (default: 0.92)
 * @returns {Promise<{ blob: Blob, dataURL: string, file: File }>}
 */
export const generateEditedImage = async (
    imageSrc,
    cropArea,
    rotation = 0,
    flip = { horizontal: false, vertical: false },
    filterCSS = 'none',
    outputFormat = 'image/jpeg',
    quality = 0.92
) => {
    const canvas = await applyEditsToCanvas(imageSrc, cropArea, rotation, flip, filterCSS);

    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Failed to generate image blob'));
                    return;
                }

                const dataURL = canvas.toDataURL(outputFormat, quality);
                const extension = outputFormat === 'image/png' ? 'png' : 'jpg';
                const file = new File([blob], `edited-image.${extension}`, { type: outputFormat });

                resolve({ blob, dataURL, file });
            },
            outputFormat,
            quality
        );
    });
};

/**
 * Generate a small thumbnail for filter preview
 * @param {string} imageSrc - Source image
 * @param {number} size - Thumbnail size in pixels (default: 80)
 * @returns {Promise<string>} - Data URL of the thumbnail
 */
export const generateThumbnail = async (imageSrc, size = 80) => {
    const image = await loadImage(imageSrc);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Crop to center square
    const minDim = Math.min(image.width, image.height);
    const sx = (image.width - minDim) / 2;
    const sy = (image.height - minDim) / 2;

    canvas.width = size;
    canvas.height = size;

    ctx.drawImage(image, sx, sy, minDim, minDim, 0, 0, size, size);

    return canvas.toDataURL('image/jpeg', 0.6);
};
